import React, { Fragment, useState } from 'react';
import { maxInitialSections, maxSwimlaneItems } from './Sections.constants';
import { loadMediaSection } from '../../api/pac/load-media-section';
import { queryKeys } from '../../config/use-query';
import { useVideoConfig } from '../../config/VideoAppContext';
import { useAxios } from '../../hooks/use-axios';
import { useItems } from '../../hooks/use-items';
import { usePacPageLink } from '../../hooks/use-pac-page-link';
import { usePaging } from '../../hooks/use-paging';
import { GridViewTitle } from '../GridView/GridViewTitle';
import { SmartGridView } from '../GridView/SmartGridView';
import { HeroStage } from '../HeroStage/HeroStage';
import { Highlight } from '../Highlight/Highlight';
import { MediaElementSwimlane } from '../MediaElementSwimlane/MediaElementSwimlane';
import { PlayContainer } from '../PlayContainer/PlayContainer';
import { Swimlane } from '../Swimlane/Swimlane';
import { SwimlaneTitle } from '../Swimlane/SwimlaneTitle';
import { Swipeable } from '../Swipeable/Swipeable';
import { GridMediaTeaser } from '../Teaser/MediaTeaser/GridMediaTeaser';
import { MediaTeaser } from '../Teaser/MediaTeaser/MediaTeaser';
import { MediaTeaserPlaceholder } from '../Teaser/MediaTeaser/MediaTeaserPlaceholder';
import { SectionDetailTeaser } from '../Teaser/SectionDetailTeaser/SectionDetailTeaser';
import { MediaElement } from '../TeaserElement/MediaElement';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
export const MediaSection = ({
  section,
  sectionIndex,
  initialData = {}
}) => {
  const [wasIntersecting, setWasIntersecting] = useState(false);
  const drawContent = sectionIndex < maxInitialSections || wasIntersecting;
  const {
    previewDate,
    usePreview
  } = useVideoConfig();
  const axios = useAxios();
  const sectionData = initialData.preloadedSections?.[section.id];
  const preloadedData = sectionData?.sectionType === 'MediaSection' ? sectionData : undefined;
  const {
    data: plainMedias,
    error,
    canLoadMore,
    loadMore,
    isLoading
  } = usePaging(queryKeys.MediaSection(section.id), next => loadMediaSection(axios, section.id, usePreview, previewDate, next), {
    initialData: preloadedData?.data ? {
      pages: [preloadedData.data],
      pageParams: []
    } : undefined
  });
  const {
    pickRandomElement,
    title,
    description,
    label,
    name
  } = section.representation;
  const medias = useItems(plainMedias, pickRandomElement, preloadedData?.randomMedias);
  const detailLink = usePacPageLink(section, medias);
  const showSection = !error && medias.length > 0 || isLoading;
  if (!showSection) {
    return /*#__PURE__*/_jsx(Fragment, {});
  }
  switch (name) {
    case 'Swimlane':
      {
        return /*#__PURE__*/_jsxs(Swimlane, {
          rootMargin: "300px",
          onIntersectionChanged: isIntersecting => {
            if (isIntersecting) {
              setWasIntersecting(isIntersecting);
            }
          },
          children: [/*#__PURE__*/_jsx(SwimlaneTitle, {
            title: title ?? '\u00A0',
            description: description,
            titleLink: detailLink
          }), /*#__PURE__*/_jsxs(Swipeable, {
            buttonPositionFactor: 9 / 16,
            placeholder: /*#__PURE__*/_jsx(MediaTeaserPlaceholder, {}),
            analyticsTitle: title,
            children: [drawContent && medias?.slice(0, maxSwimlaneItems)?.map((video, index) => /*#__PURE__*/_jsx(MediaTeaser, {
              media: video,
              index: index,
              labelOrder: ['live', 'expiringSoon'],
              showWatchLaterUndo: true
            }, video.id)), drawContent && medias.length > maxSwimlaneItems && /*#__PURE__*/_jsx(SectionDetailTeaser, {
              section: section,
              aspectRatio: '16/9'
            })]
          })]
        });
      }
    case 'Grid':
      {
        return /*#__PURE__*/_jsxs(PlayContainer, {
          children: [/*#__PURE__*/_jsx(GridViewTitle, {
            title: title ?? ''
          }), /*#__PURE__*/_jsx(SmartGridView, {
            canLoadMore: canLoadMore,
            loadMore: loadMore,
            analyticsTitle: title,
            children: medias?.map((media, index) => /*#__PURE__*/_jsx(GridMediaTeaser, {
              media: media,
              index: index,
              labelOrder: ['live', 'expiringSoon']
            }, media.id))
          })]
        });
      }
    case 'HeroStage':
      {
        return /*#__PURE__*/_jsx(HeroStage, {
          medias: medias
        });
      }
    case 'MediaElement':
      {
        const [media] = medias;
        return /*#__PURE__*/_jsx(MediaElement, {
          media: media,
          title: title,
          labelText: label,
          index: 0
        });
      }
    case 'MediaElementSwimlane':
      {
        return /*#__PURE__*/_jsx(MediaElementSwimlane, {
          medias: medias,
          title: title
        });
      }
    case 'Highlight':
      {
        return /*#__PURE__*/_jsx(Highlight, {
          highlightElementType: 'Highlight',
          detailLink: detailLink ?? '',
          title: title,
          description: description,
          imageUrl: section.representation.imageUrl,
          focalPoint: section.representation.focalPoint
        });
      }
    default:
      {
        return /*#__PURE__*/_jsx(Fragment, {});
      }
  }
};